import React, { useEffect, useState, useMemo } from "react";
import { format } from "date-fns";

import { SearchableSelect, LineChartTimeline, Lottie } from "../..";
import { labMachineAnalysisUseStyle } from "../../../utils/styles";
import {
  MACHINE_ANAYLYSIS_TABLE,
  SAMPLE_ANALYSIS_TABLE,
} from "../../../utils/constants";
import chartLoading from "../../../assets/lotties/29444-graph-animation.json";
import { Title, SubTitle } from "./Title";
import { Sample } from "./types";

const CHART_PROPS = {
  HEIGHT: 350,
};

export function Charts({
  data,
  type,
}: {
  data: Sample[];
  type?: "component" | "engine";
}): any {
  const classes = labMachineAnalysisUseStyle();

  const [dataToShow, setDataToShow] = useState([]);
  const [machine, setMachine] = useState("");
  const [keyLabelToPlot, setKeyLabelToPlot] = useState<
    { label: string; key: string }[]
  >([]);

  useEffect(() => {
    setMachine("");
  }, [data]);

  useEffect(() => {
    if (!machine) setDataToShow([]);

    const keys = SAMPLE_ANALYSIS_TABLE.HEADERS.reduce(
      (acc, item) => (item.customChart ? [...acc, item.key] : acc),
      [],
    );

    const dataFiltered = data.filter((i) => i.equipo_componente === machine);
    const propAndValues = dataFiltered.reduce((acc, item) => {
      for (const key of keys) {
        const newValue = {
          x: format(new Date(item.fecha_muestra), "dd-MM-yyyy"),
          y: item[key],
        };

        if (
          newValue.x &&
          typeof newValue.x === "string" &&
          typeof newValue.y === "number"
        )
          acc[key] = acc[key] ? [...acc[key], newValue] : [newValue];
      }

      return acc;
    }, {});

    const formatted = Object.keys(propAndValues).map((key) => ({
      id: key,
      data: propAndValues[key],
    }));

    setDataToShow(formatted);
  }, [machine]);

  const machineOpts = useMemo(() => {
    const machineMap = data.reduce(
      (acc, { equipo_componente }) =>
        acc[equipo_componente] ? acc : { ...acc, [equipo_componente]: true },
      {},
    );
    return Object.keys(machineMap);
  }, [data]);

  const chartViscSootData = useMemo(() => {
    return dataToShow.filter((i) =>
      type === "engine"
        ? ["soot", "visc"].includes(i.id)
        : ["visc"].includes(i.id),
    );
  }, [dataToShow, type]);

  const chartViscSootProps = type === "engine" ? ViscSootChartProps : {};

  return (
    <>
      <Title>Gráficos</Title>

      <MachineForm
        options={machineOpts}
        onChange={setMachine}
        value={machine}
      />

      <SubTitle>Gráfico Personalizable</SubTitle>

      <SearchableSelect
        label="Elemento a graficar"
        className={classes.spaceBetweenInputs}
        options={SAMPLE_ANALYSIS_TABLE.HEADERS.filter((i) => i.customChart)}
        getOptionLabel={(opt: any) => opt.label}
        onChange={(opt: any) => setKeyLabelToPlot(opt)}
        value={keyLabelToPlot}
        multiple
      />

      {keyLabelToPlot.length > 0 ? (
        <div style={{ width: "100%", height: CHART_PROPS.HEIGHT }}>
          <LineChartTimeline
            data={dataToShow.filter((i) =>
              keyLabelToPlot.map((i) => i.key).includes(i.id),
            )}
            curve="linear"
            axisLeftLegend="Concentración"
            pointSize={4}
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            height: CHART_PROPS.HEIGHT,
          }}>
          <Lottie animationData={chartLoading} height={200} />
        </div>
      )}

      <SubTitle>
        Gráfico - Viscosidad {type === "engine" && <span>y Hollín</span>}
      </SubTitle>

      <div style={{ width: "100%", height: CHART_PROPS.HEIGHT }}>
        <LineChartTimeline
          data={chartViscSootData}
          curve="linear"
          axisLeftLegend="Viscosidad"
          {...chartViscSootProps}
        />
      </div>

      <SubTitle>Gráfico - Elementos de desgastes y Contaminantes</SubTitle>

      <div style={{ width: "100%", height: CHART_PROPS.HEIGHT }}>
        <LineChartTimeline
          data={dataToShow.filter((i) =>
            ["fe", "si", "al", "cu", "pb"].includes(i.id),
          )}
          curve="linear"
          axisLeftLegend="Concentración"
          pointSize={4}
        />
      </div>

      <SubTitle>Gráfico - Aditivos</SubTitle>

      <div
        style={{
          width: "100%",
          height: CHART_PROPS.HEIGHT,
        }}>
        <LineChartTimeline
          data={dataToShow.filter((i) =>
            ["ca", "zn", "p", "mg"].includes(i.id),
          )}
          curve="linear"
          axisLeftLegend="Concentración"
          pointSize={4}
        />
      </div>
    </>
  );
}

function MachineForm({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string | null;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <>
      <SubTitle>Filtros</SubTitle>

      <SearchableSelect
        label="Equipos"
        value={value}
        options={options}
        getOptionLabel={(v: string) => v}
        onChange={onChange}
        style={{
          marginTop: 20,
          maxHeight: 200,
          overflowX: "auto",
          paddingTop: 5,
          paddingBottom: 5,
        }}
      />
    </>
  );
}

const ViscSootChartProps = {
  markers: [
    {
      axis: "y",
      value: MACHINE_ANAYLYSIS_TABLE.MARKERS.SOOT_LIMIT.VALUE,
      lineStyle: { stroke: "#b0413e", strokeWidth: 2 },
      textStyle: { fontWeight: "bold" },
      legend: `${MACHINE_ANAYLYSIS_TABLE.MARKERS.SOOT_LIMIT.LABEL} (${MACHINE_ANAYLYSIS_TABLE.MARKERS.SOOT_LIMIT.VALUE})`,
      legendOrientation: "right",
    },
  ],
  pointSize: 4,
};
